import moment from "moment-timezone"

import type {
    IOneVerifyPayerStatusIncident,
    IOneVerifyPayerStatusPayer,
} from "@common-shared/types/responseData/oneVerifyResponseData.types"

import {
    PAYER_STATUS_CONFIG,
    PayerStatusRange,
} from "../config/payerStatus.config"

export class PayerStatusHelper {
    /**
     * Get payer name, payer ID when we don't have a canonical payer for it
     */
    static getPayerName(
        row: Pick<IOneVerifyPayerStatusPayer, "payerId" | "payer">,
    ): string {
        return row.payer?.insuranceName || row.payerId
    }

    /**
     * Compare payers by name, payers we don't have a canonical payer for go last, by payer ID
     */
    static comparePayersByName(
        a: IOneVerifyPayerStatusPayer,
        b: IOneVerifyPayerStatusPayer,
    ): number {
        return (
            Number(!a.payer?.insuranceName) - Number(!b.payer?.insuranceName) ||
            PayerStatusHelper.getPayerName(a).localeCompare(
                PayerStatusHelper.getPayerName(b),
                undefined,
                { numeric: true },
            )
        )
    }

    /**
     * Get when range starts, range ends when data was fetched
     */
    static getRangeStart(range: PayerStatusRange, fetchedAt: number): number {
        return fetchedAt - PAYER_STATUS_CONFIG.rangesDurationMs[range]
    }

    /**
     * Get incidents that overlap range, ongoing incident always does
     */
    static getIncidentsInRange(
        incidents: IOneVerifyPayerStatusIncident[],
        rangeStart: number,
    ): IOneVerifyPayerStatusIncident[] {
        return incidents.filter(
            incident =>
                !incident.recoveredAt || incident.recoveredAt > rangeStart,
        )
    }

    /**
     * Get when incident happened, e.g. "Oct 14, 3:00 PM – 4:05 PM" or "Oct 14, 3:00 PM – now"
     */
    static getIncidentPeriodLabel(
        incident: IOneVerifyPayerStatusIncident,
    ): string {
        return `${moment(incident.startedAt).format(
            PAYER_STATUS_CONFIG.dayTimeFormat,
        )} – ${PayerStatusHelper.getIncidentEndLabel(incident)}`
    }

    /**
     * Get when incident recovered, only time when it's the day it started, e.g. "4:05 PM", "Oct 15, 1:10 AM" or "now"
     */
    static getIncidentEndLabel(
        incident: IOneVerifyPayerStatusIncident,
    ): string {
        if (!incident.recoveredAt) {
            return "now"
        }

        const recoveredAt = moment(incident.recoveredAt)

        return recoveredAt.format(
            recoveredAt.isSame(incident.startedAt, "day")
                ? PAYER_STATUS_CONFIG.timeFormat
                : PAYER_STATUS_CONFIG.dayTimeFormat,
        )
    }

    /**
     * Get how long incident lasted, ongoing incident lasts until data was fetched, e.g. "1h 5m" or "2h"
     */
    static getIncidentDurationLabel(
        incident: IOneVerifyPayerStatusIncident,
        fetchedAt: number,
    ): string {
        // At least a minute, so very short incidents don't show as "0m"
        const totalMinutes = Math.max(
            1,
            Math.round(
                ((incident.recoveredAt || fetchedAt) - incident.startedAt) /
                    (60 * 1000),
            ),
        )

        const hours = Math.floor(totalMinutes / 60)
        const minutes = totalMinutes % 60

        if (!hours) {
            return `${minutes}m`
        }

        return minutes ? `${hours}h ${minutes}m` : `${hours}h`
    }

    /**
     * Get time relative to today, e.g. "3:42 PM today", "yesterday at 3:42 PM" or "Oct 12, 3:42 PM"
     */
    static getRelativeTimeLabel(timestamp: number): string {
        const time = moment(timestamp)

        if (time.isSame(moment(), "day")) {
            return `${time.format(PAYER_STATUS_CONFIG.timeFormat)} today`
        }

        if (time.isSame(moment().subtract(1, "day"), "day")) {
            return `yesterday at ${time.format(PAYER_STATUS_CONFIG.timeFormat)}`
        }

        return time.format(PAYER_STATUS_CONFIG.dayTimeFormat)
    }

    /**
     * Get issues count, e.g. "no issues", "1 issue" or "3 issues"
     */
    static getIssuesCountLabel(count: number): string {
        if (!count) {
            return "no issues"
        }

        return `${count} ${count === 1 ? "issue" : "issues"}`
    }
}
